import * as THREE from "three";
import { instantiate, loadModel, type ModelInstance } from "../assets/loader.js";
import { createWorkshopEnvironment, createWorkshopStage, disposeWorkshopStage } from "./UnitViewer.js";
import { unitById } from "@zend/content";
import { audio } from "../audio/engine.js";
import type { Cue } from "../audio/score.js";

/**
 * The fabrication chamber (R27): the frame assembles under tier-coloured light, an S-tier gets a gold
 * pre-flash, then the unit idles on the pedestal. One WebGL context per reveal overlay.
 */
export class RevealScene {
  private readonly renderer: THREE.WebGLRenderer;
  private readonly scene = new THREE.Scene();
  private readonly camera: THREE.PerspectiveCamera;
  private readonly accent: THREE.PointLight;
  private readonly rings: THREE.Mesh[] = [];
  private readonly flash: THREE.Mesh;
  private readonly stage: ReturnType<typeof createWorkshopStage>;
  private readonly environment: THREE.WebGLRenderTarget;
  private readonly particles: THREE.Points<THREE.BufferGeometry, THREE.PointsMaterial>;
  private readonly modelMount = new THREE.Group();
  private modelHeight = 2.0;
  private preflashDuration = 0.3;
  private instance: ModelInstance | null = null;
  private raf = 0;
  private last = 0;
  private time = 0;
  private phase: "idle" | "assemble" | "flash" | "show" = "idle";
  private phaseStart = 0;
  private token = 0;
  private disposed = false;
  private onDone: (() => void) | null = null;
  private revealCue: Cue = "reveal.D";
  private sounded = false;

  constructor(private readonly canvas: HTMLCanvasElement) {
    this.renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true });
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.0;
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.setPixelRatio(Math.min(2, window.devicePixelRatio || 1));
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFShadowMap;
    this.scene.background = new THREE.Color(0xd3d7cc);
    this.scene.fog = new THREE.Fog(0xd3d7cc, 10, 22);
    this.environment = createWorkshopEnvironment(this.renderer);
    this.scene.environment = this.environment.texture;
    this.scene.environmentIntensity = 0.75;
    this.camera = new THREE.PerspectiveCamera(34, 1, 0.1, 100);
    this.scene.add(new THREE.HemisphereLight(0xd8e9eb, 0xb0a080, 1.2));
    const key = new THREE.DirectionalLight(0xffedcf, 1.9);
    key.position.set(-3, 6, 4);
    key.castShadow = true;
    key.shadow.mapSize.set(1024, 1024);
    key.shadow.camera.left = -3;
    key.shadow.camera.right = 3;
    key.shadow.camera.top = 4;
    key.shadow.camera.bottom = -2;
    key.shadow.normalBias = 0.035;
    this.scene.add(key);
    this.accent = new THREE.PointLight(0x72b4b4, 8, 8, 2);
    this.accent.position.set(-1.8, 2.0, -1.2);
    this.scene.add(this.accent);
    this.stage = createWorkshopStage("#72b4b4");
    this.scene.add(this.stage.group, this.modelMount);
    this.modelMount.position.y = 0.035;
    for (let i = 0; i < 3; i++) {
      const ring = new THREE.Mesh(new THREE.TorusGeometry(1.34 + i * 0.12, 0.009, 6, 64), new THREE.MeshStandardMaterial({ color: 0x599c9d, emissive: new THREE.Color(0x72b4b4), emissiveIntensity: 0.4, transparent: true, opacity: 0.8 }));
      ring.rotation.x = Math.PI / 2;
      ring.position.y = -0.02 - i * 0.06;
      this.scene.add(ring);
      this.rings.push(ring);
    }
    this.flash = new THREE.Mesh(new THREE.CircleGeometry(1.20, 48), new THREE.MeshBasicMaterial({ color: 0xffe7b6, transparent: true, opacity: 0, depthWrite: false }));
    this.flash.rotation.x = -Math.PI / 2;
    this.flash.position.y = 0.036;
    this.scene.add(this.flash);
    const points = new Float32Array(72 * 3);
    for (let i = 0; i < 72; i++) {
      const angle = i * 2.399963;
      const radius = 1.15 + (i % 7) * 0.07;
      points[i * 3] = Math.sin(angle) * radius;
      points[i * 3 + 1] = 0.2 + (i % 13) * 0.17;
      points[i * 3 + 2] = Math.cos(angle) * radius;
    }
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute("position", new THREE.BufferAttribute(points, 3));
    this.particles = new THREE.Points(geometry, new THREE.PointsMaterial({ color: 0xf2cc83, size: 0.027, transparent: true, opacity: 0, depthWrite: false }));
    this.scene.add(this.particles);
    this.resize();
    this.raf = requestAnimationFrame(this.loop);
  }

  resize(): void {
    const w = Math.max(1, this.canvas.clientWidth);
    const h = Math.max(1, this.canvas.clientHeight);
    this.camera.aspect = w / h;
    const halfFov = THREE.MathUtils.degToRad(this.camera.fov / 2);
    const distance = Math.max(1.65 / (Math.tan(halfFov) * this.camera.aspect), (this.modelHeight + 1.3) / (2 * Math.tan(halfFov))) * 1.10;
    const targetHeight = Math.max(0.75, this.modelHeight * 0.56);
    this.camera.position.set(0, targetHeight + distance * 0.19, distance);
    this.camera.lookAt(0, targetHeight, 0);
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(w, h, false);
  }

  /** Plays one reveal: light in the tier colour, gold pre-flash for S, assembly, idle. Resolves when the unit stands. */
  reveal(unitId: string, tierColor: string, isS: boolean, onDone: () => void): void {
    this.silence();
    this.revealCue = `reveal.${unitById(unitId).tier}`;
    this.sounded = false;
    audio.play("pull.tension");
    const token = ++this.token;
    this.onDone = onDone;
    this.phase = "flash";
    this.phaseStart = this.time;
    this.preflashDuration = isS ? 0.50 : 0.30;
    this.modelMount.scale.setScalar(0.01);
    this.modelMount.rotation.y = 0;
    this.accent.color.set(isS ? "#f5c542" : tierColor);
    this.stage.accent.color.set(tierColor);
    this.stage.accent.emissive.set(tierColor);
    this.particles.material.color.set(isS ? "#f1c778" : tierColor);
    for (const r of this.rings) (r.material as THREE.MeshStandardMaterial).emissive.set(isS ? "#f5c542" : tierColor);
    (this.flash.material as THREE.MeshBasicMaterial).color.set(isS ? "#ffd96a" : tierColor);
    if (this.instance) {
      this.instance.mixer.stopAllAction();
      this.instance.mixer.uncacheRoot(this.instance.object);
      this.modelMount.remove(this.instance.object);
      this.instance = null;
    }
    void loadModel(unitId).then((model) => {
      if (this.disposed || token !== this.token) return;
      const inst = instantiate(model);
      const box = new THREE.Box3().setFromObject(inst.object);
      const size = box.getSize(new THREE.Vector3());
      const center = box.getCenter(new THREE.Vector3());
      const scale = 2.2 / Math.max(size.x, size.y, size.z, 0.01);
      inst.object.scale.setScalar(scale);
      inst.object.position.set(-center.x * scale, -box.min.y * scale, -center.z * scale);
      this.modelHeight = size.y * scale;
      inst.play("idle");
      this.modelMount.add(inst.object);
      this.instance = inst;
      this.accent.color.set(tierColor);
      for (const r of this.rings) (r.material as THREE.MeshStandardMaterial).emissive.set(tierColor);
      // A skip while loading stays skipped when the model arrives.
      if (this.phase === "show") this.modelMount.scale.setScalar(1);
      this.resize();
    });
  }

  skip(): void {
    this.modelMount.scale.setScalar(1);
    if (this.phase !== "show") {
      this.soundReveal();
      this.phase = "show";
      this.onDone?.();
      this.onDone = null;
    }
  }

  silence(): void {
    audio.stop("pull.tension");
    audio.stop(this.revealCue);
  }

  private soundReveal(): void {
    if (this.sounded) return;
    this.sounded = true;
    audio.stop("pull.tension");
    audio.play(this.revealCue);
  }

  private readonly loop = (now: number) => {
    if (this.disposed) return;
    this.raf = requestAnimationFrame(this.loop);
    const dt = Math.min(0.1, (now - this.last) / 1000);
    this.last = now;
    this.time += dt;
    const t = this.time - this.phaseStart;
    const flashMat = this.flash.material as THREE.MeshBasicMaterial;
    if (this.phase === "flash") {
      flashMat.opacity = Math.max(0, 0.38 * (1 - t / this.preflashDuration));
      this.flash.scale.setScalar(0.75 + Math.min(1, t / this.preflashDuration) * 0.25);
      this.accent.intensity = 10 + 10 * Math.max(0, 1 - t / this.preflashDuration);
      this.particles.material.opacity = 0.3 * Math.max(0, 1 - t / this.preflashDuration);
      if (this.instance && t >= this.preflashDuration) {
        this.phase = "assemble";
        this.phaseStart = this.time;
      }
    } else if (this.phase === "assemble" && this.instance) {
      const p = Math.min(1, t / 0.9);
      const eased = 1 - Math.pow(1 - p, 3);
      this.modelMount.scale.setScalar(Math.max(0.01, eased));
      this.modelMount.rotation.y = -0.35 + (1 - eased) * Math.PI * 0.7;
      flashMat.opacity = (1 - p) * 0.20;
      this.flash.scale.setScalar(1);
      this.accent.intensity = 8 + (1 - p) * 10;
      this.particles.material.opacity = (1 - p) * 0.55;
      this.particles.position.y = p * 0.40;
      if (p >= 1) {
        this.soundReveal();
        this.phase = "show";
        this.onDone?.();
        this.onDone = null;
      }
    } else if (this.phase === "show") {
      flashMat.opacity = 0;
      this.particles.material.opacity = 0;
      this.accent.intensity = 7;
      if (this.instance) {
        this.modelMount.rotation.y += dt * 0.3;
        this.instance.mixer.update(dt);
      }
    }
    this.rings.forEach((r, i) => {
      r.rotation.z += dt * (0.3 + i * 0.2) * (i % 2 ? -1 : 1);
      // Assembly traces settle into the deck; no idle band runs across the model's face or weapon.
      r.position.y = -0.02 - i * 0.06;
    });
    this.renderer.render(this.scene, this.camera);
  };

  dispose(): void {
    this.silence();
    this.disposed = true;
    ++this.token;
    this.onDone = null;
    cancelAnimationFrame(this.raf);
    this.instance?.mixer.stopAllAction();
    if (this.instance) this.instance.mixer.uncacheRoot(this.instance.object);
    disposeWorkshopStage(this.stage.group);
    for (const mesh of [...this.rings, this.flash]) {
      mesh.geometry.dispose();
      (mesh.material as THREE.Material).dispose();
    }
    this.particles.geometry.dispose();
    this.particles.material.dispose();
    this.environment.dispose();
    this.renderer.dispose();
  }
}
